import React, { useState } from "react";
import { Modal, Alert, Button as BSButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "components/base/Button";
import { useEmpresas } from "providers/EmpresaProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { balancoService, ExcluirProdutoBalancoDto } from "services/balancoService";
import { balancoKeys } from "hooks/useBalanco";

interface BotaoExcluirProdutoProps {
    produto: {
        produtoId: number;
        nomeProduto: string;
        lote?: string;
        quantidade: number;
    };
    disabled?: boolean;
    size?: "sm" | "lg";
}

const BotaoExcluirProduto: React.FC<BotaoExcluirProdutoProps> = ({
    produto,
    disabled = false,
    size = "sm"
}) => {
    const [showModal, setShowModal] = useState(false);

    // Hooks
    const queryClient = useQueryClient();
    const { selectedIds } = useEmpresas();
    const { getMutationErrorHandler } = usePermissionModal();
    const empresaId = selectedIds[0];

    // Mutation para excluir produto
    const excluirProdutoMutation = useMutation({
        mutationFn: (dto: ExcluirProdutoBalancoDto) =>
            balancoService.excluirProduto(dto, empresaId),
        onSuccess: () => {
            setShowModal(false);
            // Invalidar cache do balanço
            queryClient.invalidateQueries({ queryKey: balancoKeys.ativo() });
        },
        onError: getMutationErrorHandler("Erro ao excluir produto do balanço.")
    });

    const handleExcluir = () => {
        const dto: ExcluirProdutoBalancoDto = {
            produtoId: produto.produtoId,
            lote: produto.lote
        };

        excluirProdutoMutation.mutate(dto);
    };

    return (
        <>
            <Button
                variant="outline-danger"
                size={size}
                onClick={() => setShowModal(true)}
                disabled={disabled}
                title="Excluir produto"
            >
                <FontAwesomeIcon icon={faTimes} />
            </Button>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger-emphasis" />
                        Excluir Produto do Balanço
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-3">
                        <div className="text-warning-emphasis">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Produto já incluído:</strong> Este produto já está no balanço. Deseja alterar a quantidade?
                        </div>
                    </Alert>

                    <div className="mb-3">
                        <p>Tem certeza que deseja excluir este produto do balanço?</p>
                        <div className="bg-body-tertiary p-3 rounded">
                            <strong>{produto.nomeProduto}</strong>
                            <div className="text-body-secondary small">
                                Quantidade: <strong>{produto.quantidade}</strong>
                                {produto.lote && (
                                    <span className="ms-2">
                                        | Lote: <span className="badge bg-secondary">{produto.lote}</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {excluirProdutoMutation.isError && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">
                                <strong>Erro:</strong> {(excluirProdutoMutation.error as Error).message}
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <BSButton
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={excluirProdutoMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Cancelar
                    </BSButton>
                    <BSButton
                        variant="danger"
                        onClick={handleExcluir}
                        disabled={excluirProdutoMutation.isPending}
                    >
                        {excluirProdutoMutation.isPending ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                                Excluindo...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTimes} className="me-2" />
                                Confirmar Exclusão
                            </>
                        )}
                    </BSButton>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BotaoExcluirProduto; 